import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, ScanLine } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function VinSearch({ large = false }) {
  const [mode, setMode] = useState("vin"); // vin | mmy
  const [vin, setVin] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  // MMY fields
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");

  const loadMakes = async () => {
    if (makes.length) return;
    const { data } = await api.get("/catalog/makes");
    setMakes(data);
  };

  const onMakeChange = async (value) => {
    setMake(value);
    setModel("");
    if (value) {
      const { data } = await api.get("/catalog/models", { params: { make: value } });
      setModels(data);
    }
  };

  const onVinSubmit = async (e) => {
    e.preventDefault();
    if (!vin.trim() || vin.length < 11) {
      toast.error("VIN must be at least 11 characters");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/vin/decode/${vin.trim()}`);
      const params = new URLSearchParams();
      if (data.make) params.set("make", data.make.split(" ")[0]); // Normalize
      if (data.model) params.set("model", data.model);
      if (data.year) params.set("year", data.year);
      params.set("vin", vin);
      toast.success(`Decoded: ${data.make || "?"} ${data.model || ""} ${data.year || ""}`);
      nav(`/search?${params.toString()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not decode VIN");
    } finally {
      setLoading(false);
    }
  };

  const onMmySubmit = (e) => {
    e.preventDefault();
    if (!make) { toast.error("Select a make"); return; }
    const params = new URLSearchParams();
    if (make) params.set("make", make);
    if (model) params.set("model", model);
    if (year) params.set("year", year);
    nav(`/search?${params.toString()}`);
  };

  const years = Array.from({ length: 25 }, (_, i) => 2025 - i);

  const containerCls = large
    ? "bg-white p-6 md:p-8 shadow-2xl border-4 border-[#121212]"
    : "bg-white p-5 border border-[#E4E4E7]";

  return (
    <div className={containerCls} data-testid="vin-search-container">
      <div className="flex gap-2 mb-5">
        <button
          onClick={() => setMode("vin")}
          className={`flex-1 py-3 text-xs md:text-sm font-bold uppercase tracking-wider border-2 transition-colors ${
            mode === "vin"
              ? "bg-[#121212] text-white border-[#121212]"
              : "bg-white text-[#121212] border-[#E4E4E7] hover:border-[#121212]"
          }`}
          data-testid="tab-vin"
        >
          <ScanLine size={14} className="inline mr-2" /> VIN Search
          <span className="ml-2 hidden md:inline text-[10px] bg-[#FFD100] text-[#121212] px-1.5 py-0.5">RECOMMENDED</span>
        </button>
        <button
          onClick={() => { setMode("mmy"); loadMakes(); }}
          className={`flex-1 py-3 text-xs md:text-sm font-bold uppercase tracking-wider border-2 transition-colors ${
            mode === "mmy"
              ? "bg-[#121212] text-white border-[#121212]"
              : "bg-white text-[#121212] border-[#E4E4E7] hover:border-[#121212]"
          }`}
          data-testid="tab-mmy"
        >
          Make · Model · Year
        </button>
      </div>

      {mode === "vin" ? (
        <form onSubmit={onVinSubmit} className="space-y-3" data-testid="vin-form">
          <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600">
            Vehicle Identification Number (VIN)
          </label>
          <div className="flex flex-col md:flex-row gap-2">
            <input
              value={vin}
              onChange={(e) => setVin(e.target.value.toUpperCase())}
              placeholder="e.g. 1HGCM82633A123456"
              maxLength={17}
              className="input-apa font-mono tracking-wider text-lg flex-1"
              data-testid="vin-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-primary min-w-[200px] !py-4"
              data-testid="vin-submit"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              Find My Parts
            </button>
          </div>
          <p className="text-xs text-zinc-500">
            Your VIN is usually on the driver-side dashboard near the windshield, or on your vehicle registration. 17 characters.
          </p>
        </form>
      ) : (
        <form onSubmit={onMmySubmit} className="space-y-3" data-testid="mmy-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">Make</label>
              <select value={make} onChange={(e) => onMakeChange(e.target.value)} className="input-apa" data-testid="make-select">
                <option value="">Select make</option>
                {makes.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">Model</label>
              <select value={model} onChange={(e) => setModel(e.target.value)} className="input-apa" disabled={!make} data-testid="model-select">
                <option value="">{make ? "Select model" : "Pick make first"}</option>
                {models.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">Year</label>
              <select value={year} onChange={(e) => setYear(e.target.value)} className="input-apa" data-testid="year-select">
                <option value="">Any year</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <button type="submit" className="btn-primary w-full md:w-auto !py-4 min-w-[220px]" data-testid="mmy-submit">
            <Search size={18} /> Search Parts
          </button>
        </form>
      )}
    </div>
  );
}
